import { StrategistResult, TradeRecommendation, formatPriceWithSymbol } from '../../types'
import {
  Brain, Zap, TrendingUp, TrendingDown, AlertCircle,
  Target, ShieldCheck, ArrowUpCircle, ArrowDownCircle,
  Scale, Percent, Clock
} from 'lucide-react'

interface Props {
  data: StrategistResult | null
  // 実際の売買シグナル(ADX/RSI/MACDルールフィルタ通過済み、ensemble方式)。
  // AI軍師(LLM)の自己申告的なtrade_recommendationとは別物で、実データで
  // バックテスト検証済みの本番シグナルそのもの。
  signal: TradeRecommendation | null
  loading: boolean
}

export default function StrategistPanel({ data, signal, loading }: Props) {
  if (loading && !data) {
    return (
      <div className="bg-trading-panel border border-trading-border rounded-lg p-5 animate-pulse">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-trading-border/50 rounded-full"></div>
          <div className="h-4 bg-trading-border/50 rounded w-1/3"></div>
        </div>
        <div className="space-y-2">
          <div className="h-3 bg-trading-border/30 rounded w-full"></div>
          <div className="h-3 bg-trading-border/30 rounded w-5/6"></div>
          <div className="h-3 bg-trading-border/30 rounded w-4/6"></div>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="bg-trading-panel border border-trading-border rounded-lg p-5 flex items-center justify-center text-trading-muted text-sm italic">
        分析データがありません
      </div>
    )
  }

  // バイアスの方向で色を変える
  const isBullish = data.bias_shift > 0
  const isBearish = data.bias_shift < 0

  const biasColor = isBullish
    ? 'text-emerald-400 bg-emerald-900/30 border-emerald-800/50'
    : isBearish
    ? 'text-rose-400 bg-rose-900/30 border-rose-800/50'
    : 'text-blue-400 bg-blue-900/30 border-blue-800/50'

  // ボラティリティ倍率の強調度合い
  const isVolatile = data.volatility_multiplier > 1.1
  const volColor = isVolatile
    ? 'text-orange-400 bg-orange-900/30 border-orange-800/50'
    : 'text-sky-400 bg-sky-900/30 border-sky-800/50'

  const rec = signal
  // entry_price/take_profit/stop_lossが揃っている実シグナルのみ表示する。
  // 実シグナルは既にADX/RSI/MACDのルールフィルタを通過済みなので、AI軍師の
  // 自己申告のような確信度(win_probability)による追加のゲートは不要。
  const hasRec = rec && rec.recommend_entry && rec.action !== 'HOLD'
    && rec.entry_price != null && rec.take_profit != null && rec.stop_loss != null

  const actionColor = rec?.action === 'BUY' ? 'text-emerald-400' : rec?.action === 'SELL' ? 'text-rose-400' : 'text-gray-400'
  const actionBg = rec?.action === 'BUY' ? 'bg-emerald-500/10 border-emerald-500/30' : rec?.action === 'SELL' ? 'bg-rose-500/10 border-rose-500/30' : 'bg-gray-800 border-gray-700'

  // AI軍師(LLM)自己申告のtrade_recommendation。同一market_typeのシャドー実績
  // (interval="ai_strategist")がensembleの実績を安定して上回ると自動判定
  // (卒業)されるまでバックエンドがnullを返すため、trade_recommendation_graduated
  // が立っている場合のみ描画対象になる(未卒業の間は従来通り非表示)。
  const aiRec = data.trade_recommendation
  const hasAiRec = data.trade_recommendation_graduated && aiRec && aiRec.action !== 'HOLD'
    && aiRec.entry_price != null && aiRec.take_profit != null && aiRec.stop_loss != null

  return (
    <div className="relative bg-trading-panel border border-trading-border/80 rounded-lg overflow-hidden group transition-all duration-300 hover:border-indigo-500/40 hover:shadow-lg hover:shadow-indigo-500/5">

      {/* グロウ効果デコレーション */}
      <div className="absolute -top-24 -right-24 w-48 h-48 bg-indigo-500/10 blur-3xl rounded-full group-hover:bg-indigo-500/20 transition-all duration-700 pointer-events-none" />

      <div className="p-5 relative z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-md bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 shadow-[0_0_10px_rgba(99,102,241,0.1)]">
              <Brain className="w-4 h-4" />
            </div>
            <h3 className="font-bold text-sm tracking-wide text-gray-100">AIアドバイザーの戦略ブリーフィング</h3>
          </div>

          <div className="flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            LIVE
          </div>
        </div>

        {/* 戦略テキスト */}
        <div className="mb-5 relative">
          <div className="absolute -left-3 top-0 bottom-0 w-[3px] bg-indigo-500/50 rounded-full" />
          <p className="text-gray-300 leading-relaxed text-[13px] tracking-normal pl-3 whitespace-pre-wrap font-medium">
            {data.strategy_memo}
          </p>
        </div>

        {/* アクティブシグナルカード (条件付き表示) */}
        {hasRec && (
          <div className={`mb-5 p-4 rounded-lg border ${actionBg} backdrop-blur-sm relative overflow-hidden`}>
            {/* バックグラウンドロゴ */}
            <div className="absolute right-0 bottom-0 opacity-[0.03] transform translate-x-4 translate-y-4">
              {rec.action === 'BUY' ? <ArrowUpCircle size={120} /> : <ArrowDownCircle size={120} />}
            </div>

            <div className="flex items-center gap-2 mb-3">
              <Target className={`w-4 h-4 ${actionColor}`} />
              <span className={`text-xs font-extrabold tracking-wider uppercase ${actionColor}`}>
                🎯 アクティブシグナル ({rec.action})
              </span>
              {rec.market_closed && (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
                  市場休場中(保有中ポジション)
                </span>
              )}
              <div className="ml-auto flex items-center gap-2">
                {rec.recorded_at != null && (
                  <span className="text-[10px] text-trading-muted/70 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    発報: {new Date(rec.recorded_at).toLocaleString('ja-JP', {
                      timeZone: 'Asia/Tokyo',
                      month: '2-digit',
                      day:   '2-digit',
                      hour:  '2-digit',
                      minute: '2-digit',
                    })} JST
                  </span>
                )}
                <div className="text-[10px] text-trading-muted flex items-center gap-1 bg-black/20 px-2 py-0.5 rounded-full border border-white/5">
                  <ShieldCheck className="w-3 h-3" /> 実データ検証済み
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 relative z-10">
              {/* 左側: 価格設定 */}
              <div className="col-span-2 grid grid-cols-3 gap-2">
                <div className="bg-black/20 p-2 rounded border border-white/5">
                  <div className="text-[10px] text-amber-400/80 mb-0.5">ENTRY (指値目安)</div>
                  <div className="text-sm font-bold text-amber-300">
                    {rec.suggested_entry_price != null
                      ? formatPriceWithSymbol(rec.suggested_entry_price, data.symbol)
                      : (rec.entry_price ? formatPriceWithSymbol(rec.entry_price, data.symbol) : '現在価格')}
                  </div>
                  {rec.suggested_entry_price != null && rec.entry_price != null && (
                    <div className="mt-1 pt-1 border-t border-white/5">
                      <div className="text-[10px] text-trading-muted mb-0.5">成行</div>
                      <div className="text-xs font-medium text-gray-400">
                        {formatPriceWithSymbol(rec.entry_price, data.symbol)}
                      </div>
                    </div>
                  )}
                </div>
                <div className="bg-emerald-500/5 p-2 rounded border border-emerald-500/20">
                  <div className="text-[10px] text-emerald-400/80 mb-0.5">TP (利確)</div>
                  <div className="text-sm font-bold text-emerald-300">
                    {rec.take_profit ? formatPriceWithSymbol(rec.take_profit, data.symbol) : '--'}
                  </div>
                </div>
                <div className="bg-rose-500/5 p-2 rounded border border-rose-500/20">
                  <div className="text-[10px] text-rose-400/80 mb-0.5">SL (損切)</div>
                  <div className="text-sm font-bold text-rose-300">
                    {rec.stop_loss ? formatPriceWithSymbol(rec.stop_loss, data.symbol) : '--'}
                  </div>
                </div>
              </div>

              {/* 右側: 期待値 */}
              <div className="flex flex-col gap-2 border-l border-white/10 pl-4">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-trading-muted flex items-center gap-1">
                    <Scale className="w-3 h-3" /> R/R比
                  </span>
                  <span className="text-xs font-bold text-gray-200">
                    {rec.risk_reward_ratio?.toFixed(2) || '--'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-trading-muted flex items-center gap-1">
                    <Percent className="w-3 h-3" /> 実績勝率
                  </span>
                  <span className={`text-xs font-bold ${rec.win_probability != null && rec.win_probability >= 0.6 ? 'text-cyan-400' : 'text-gray-200'}`}>
                    {rec.win_probability != null ? `${(rec.win_probability * 100).toFixed(0)}%` : '--'}
                  </span>
                </div>
              </div>
            </div>

            {/* 注意書き */}
            <div className="mt-3 text-[9px] text-trading-muted/60 leading-tight">
              ※ ADX/RSI/MACDのルールフィルタを通過した実際の売買シグナルです(AIアドバイザーの見解とは別)。実績勝率は同銘柄の過去の解決済みトレードに基づく実測値で、サンプル数が少ない場合は「--」と表示されます。ENTRY(指値目安)は、シグナル発生直後に一旦逆行してから予測方向へ転じた過去の実績を踏まえた参考値です。TP/SLの水準はシグナル発生時点の成行価格を基準に算出したもので、指値目安の影響は受けません。市況により急変するため、実際の取引は自己の判断で行ってください。
            </div>
          </div>
        )}

        {/* AI軍師の提案価格帯(統計検証をクリアした資産クラスのみ表示・参考情報) */}
        {hasAiRec && aiRec && (
          <div className="mb-5 p-3 rounded-lg border border-indigo-500/20 bg-indigo-500/5">
            <div className="flex items-center gap-2 mb-2">
              <Brain className="w-3.5 h-3.5 text-indigo-400" />
              <span className="text-[11px] font-bold tracking-wide text-indigo-300">
                AIアドバイザーの提案価格帯 ({aiRec.action})
              </span>
              <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">
                精度実績あり
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <div className="bg-black/20 p-2 rounded border border-white/5">
                <div className="text-[10px] text-indigo-300/70 mb-0.5">ENTRY</div>
                <div className="text-sm font-bold text-gray-200">
                  {formatPriceWithSymbol(aiRec.entry_price!, data.symbol)}
                </div>
              </div>
              <div className="bg-emerald-500/5 p-2 rounded border border-emerald-500/20">
                <div className="text-[10px] text-emerald-400/80 mb-0.5">TP (利確)</div>
                <div className="text-sm font-bold text-emerald-300">
                  {formatPriceWithSymbol(aiRec.take_profit!, data.symbol)}
                </div>
              </div>
              <div className="bg-rose-500/5 p-2 rounded border border-rose-500/20">
                <div className="text-[10px] text-rose-400/80 mb-0.5">SL (損切)</div>
                <div className="text-sm font-bold text-rose-300">
                  {formatPriceWithSymbol(aiRec.stop_loss!, data.symbol)}
                </div>
              </div>
            </div>
            <div className="text-[9px] text-trading-muted/60 leading-tight">
              ※ AIアドバイザーが独自に提示した価格帯です。この資産クラスでは過去の実績が上の「アクティブシグナル」を上回ることを継続的に確認できているため参考表示しています。とはいえ両者は別ロジックによる見解であり、実際の取引は自己の判断で行ってください。
            </div>
          </div>
        )}

        {/* 定量パラメータタグ群 */}
        <div className="flex flex-wrap gap-2 pt-3 border-t border-trading-border/50">
          <div className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border ${volColor}`}>
            <Zap className="w-3 h-3" />
            <span className="opacity-80">Vol倍率:</span>
            <span className="font-bold">x{data.volatility_multiplier.toFixed(2)}</span>
          </div>

          <div className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border ${biasColor}`}>
            {isBullish ? <TrendingUp className="w-3 h-3" /> : isBearish ? <TrendingDown className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
            <span className="opacity-80">トレンドバイアス:</span>
            <span className="font-bold">{(data.bias_shift > 0 ? '+' : '')}{data.bias_shift.toFixed(4)}</span>
          </div>

          <div className="ml-auto flex flex-col items-end gap-0.5 mt-auto pt-1">
            <span className="text-[10px] text-trading-muted/70">適応型モンテカルロ適用中</span>
            {data.updated_at > 0 && (
              <span className="flex items-center gap-1 text-[10px] text-trading-muted/50">
                <Clock className="w-2.5 h-2.5" />
                更新: {new Date(data.updated_at * 1000).toLocaleString('ja-JP', {
                  timeZone: 'Asia/Tokyo',
                  month: '2-digit',
                  day:   '2-digit',
                  hour:  '2-digit',
                  minute:'2-digit',
                })} JST
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
